import { useState, useEffect, useRef } from 'react'

// ══════════════════════════════════════════════════════════
// CONFIG
// ══════════════════════════════════════════════════════════
const ABONNEMENT = { type: 'annuel', name: 'Adhésion 100% Johnny', price: 20 }

const DONATION_OPTIONS = [
  { label: 'Sans don', amount: 0  },
  { label: '+ 5€',    amount: 5  },
  { label: '+ 10€',   amount: 10 },
  { label: '+ 20€',   amount: 20 },
  { label: 'Autre',   amount: -1 },
]

const STEPS = {
  LANDING:  'landing',
  DONATION: 'donation',
  CART:     'cart',
  ORDER:    'order',
  PAYMENT:  'payment',
  SUCCESS:  'success',
}

const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY

function generateRef() {
  const d   = new Date()
  const y   = d.getFullYear()
  const m   = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const r   = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `REF-ASSO-${y}${m}${day}-${r}`
}

// ══════════════════════════════════════════════════════════
// ATOMS
// ══════════════════════════════════════════════════════════
function StepBadge({ children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 12px', borderRadius: 20,
      background: 'rgba(255,215,0,0.12)', border: '1px solid rgba(255,215,0,0.4)',
      color: '#ffd700', fontSize: '0.7rem', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '1px',
    }}>{children}</span>
  )
}

function GoldBtn({ children, onClick, style = {}, disabled = false, variant = 'primary' }) {
  const [h, setH] = useState(false)
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '13px 26px', borderRadius: 50, fontWeight: 800, fontSize: '0.88rem',
    cursor: disabled ? 'not-allowed' : 'pointer', outline: 'none',
    transition: 'all 0.18s', border: 'none', opacity: disabled ? 0.45 : 1,
    transform: h && !disabled ? 'translateY(-2px)' : 'none', ...style,
  }
  const styles = {
    primary: {
      background: h ? 'linear-gradient(135deg,#ffe566,#ffd700)' : 'linear-gradient(135deg,#ffd700,#e6b800)',
      color: '#000',
      boxShadow: h ? '0 8px 28px rgba(255,215,0,0.55)' : '0 4px 16px rgba(255,215,0,0.3)',
    },
    ghost: {
      background: h ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.04)',
      color: '#ffd700', border: '1px solid rgba(255,215,0,0.35)',
    },
  }
  return (
    <button style={{ ...base, ...styles[variant] }} onClick={onClick}
      disabled={disabled} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
      {children}
    </button>
  )
}

function Card({ children, style = {}, glow = false }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${glow ? 'rgba(255,215,0,0.4)' : 'rgba(255,255,255,0.09)'}`,
      borderRadius: 18, padding: 'clamp(18px,2.5vw,28px)',
      boxShadow: glow ? '0 0 36px rgba(255,215,0,0.07)' : '0 4px 20px rgba(0,0,0,0.25)',
      backdropFilter: 'blur(10px)', ...style,
    }}>{children}</div>
  )
}

function FieldInput({ label, id, type = 'text', required, value, onChange, placeholder, autoFilled }) {
  const [focus, setFocus] = useState(false)
  const hasValue = value && value.trim().length > 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <label htmlFor={id} style={{ fontSize: '0.74rem', fontWeight: 700, color: 'rgba(255,255,255,0.52)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </label>
        {required && <span style={{ color: '#ffd700', fontSize: '0.7rem' }}>*</span>}
        {autoFilled && hasValue && (
          <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#34d399', background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 6, padding: '1px 5px' }}>
            ✓ Auto
          </span>
        )}
      </div>
      <input id={id} type={type} required={required} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{
          background: autoFilled && hasValue ? 'rgba(52,211,153,0.05)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${focus ? 'rgba(255,215,0,0.55)' : autoFilled && hasValue ? 'rgba(52,211,153,0.3)' : 'rgba(255,255,255,0.11)'}`,
          borderRadius: 10, padding: '11px 14px', color: '#fff',
          fontSize: '0.9rem', outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s',
        }}
      />
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// 🛒 FLOATING CART — positionné AU-DESSUS de la DockBar (68px)
// ══════════════════════════════════════════════════════════
function CartIcon({ count, onClick }) {
  const [pulse, setPulse] = useState(false)
  useEffect(() => {
    if (count > 0) { setPulse(true); const t = setTimeout(() => setPulse(false), 600); return () => clearTimeout(t) }
  }, [count])
  if (count === 0) return null
  return (
    <button onClick={onClick} style={{
      position: 'fixed',
      bottom: 74,   // ← au-dessus de la DockBar (qui fait ~68px)
      right: 16,
      zIndex: 500,  // ← sous la DockBar (zIndex ~1000)
      width: 52, height: 52, borderRadius: '50%',
      background: 'linear-gradient(135deg,#ffd700,#e6b800)',
      border: '2px solid rgba(0,0,0,0.2)',
      cursor: 'pointer', outline: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 20px rgba(255,215,0,0.5)',
      transform: pulse ? 'scale(1.2)' : 'scale(1)',
      transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <span style={{ fontSize: '1.3rem' }}>🛒</span>
      <div style={{
        position: 'absolute', top: -5, right: -5,
        width: 20, height: 20, borderRadius: '50%',
        background: '#dc143c', color: '#fff',
        fontSize: '0.62rem', fontWeight: 900,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid #0a0a0a',
      }}>{count}</div>
    </button>
  )
}

// ══════════════════════════════════════════════════════════
// ✅ PAYMENT METHOD ROW — DIV au lieu de BUTTON pour éviter
//    que le CSS global force le fond jaune sur les <button>
// ══════════════════════════════════════════════════════════
function PayMethodRow({ m, selected, onClick }) {
  const [hover, setHover] = useState(false)
  const sel = selected === m.id
  return (
    <div
      role="radio"
      aria-checked={sel}
      tabIndex={0}
      onClick={() => onClick(m.id)}
      onKeyDown={e => e.key === 'Enter' && onClick(m.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '15px 17px', borderRadius: 13,
        cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.15s',
        // ✅ JAMAIS jaune, toujours fond sombre
        background: sel
          ? 'rgba(15,15,25,0.95)'
          : hover
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(255,255,255,0.03)',
        border: `2px solid ${sel ? '#ffd700' : hover ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.09)'}`,
        boxShadow: sel ? '0 0 18px rgba(255,215,0,0.12), inset 0 0 0 1px rgba(255,215,0,0.08)' : 'none',
        userSelect: 'none',
      }}
    >
      {/* Icône */}
      <div style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: m.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {m.iconContent}
      </div>
      <div style={{ flex: 1 }}>
        {/* ✅ Texte toujours lisible sur fond sombre */}
        <div style={{ fontWeight: 800, fontSize: '0.92rem', color: sel ? '#ffd700' : '#fff', transition: 'color 0.15s' }}>
          {m.label}
        </div>
        <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.42)', marginTop: 2 }}>
          {m.desc}
        </div>
      </div>
      {/* Radio indicator */}
      <div style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${sel ? '#ffd700' : 'rgba(255,255,255,0.2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'border-color 0.15s',
        background: 'transparent',
      }}>
        {sel && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ffd700' }} />}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// STRIPE FORM — charge stripe.js dynamiquement puis monte
//               les éléments sur les refs DOM
// ══════════════════════════════════════════════════════════
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
    const s = document.createElement('script')
    s.src = src; s.async = true
    s.onload = resolve; s.onerror = reject
    document.head.appendChild(s)
  })
}

function StripeForm({ total, reference, orderInfo, onSuccess }) {
  const [cardName,    setCardName]    = useState('')
  const [loading,     setLoading]     = useState(false)
  const [error,       setError]       = useState('')
  const [stripeReady, setStripeReady] = useState(false)
  const [status,      setStatus]      = useState('loading') // loading | ready | error

  const stripeRef   = useRef(null)
  const elementsRef = useRef(null)
  const cardNumRef  = useRef(null)
  const cardExpRef  = useRef(null)
  const cardCvcRef  = useRef(null)

  // DOM refs — on monte les éléments Stripe directement sur ces divs
  const numDomRef = useRef(null)
  const expDomRef = useRef(null)
  const cvcDomRef = useRef(null)

  useEffect(() => {
    let destroyed = false

    const init = async () => {
      try {
        // 1. Charger stripe.js si pas déjà là
        await loadScript('https://js.stripe.com/v3/')
        if (destroyed) return

        // 2. Instancier Stripe
        stripeRef.current = window.Stripe(STRIPE_PUBLIC_KEY)
        elementsRef.current = stripeRef.current.elements({
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#ffd700', colorBackground: '#111120',
              colorText: '#ffffff',    colorDanger: '#ff6b6b',
              fontFamily: 'system-ui, sans-serif', borderRadius: '8px',
            },
          },
        })

        const baseStyle = {
          style: {
            base: {
              fontSize: '15px', color: '#fff',
              '::placeholder': { color: 'rgba(255,255,255,0.38)' },
            },
            invalid: { color: '#ff6b6b' },
          },
        }

        // 3. Créer les 3 éléments
        const cardNum = elementsRef.current.create('cardNumber', baseStyle)
        const cardExp = elementsRef.current.create('cardExpiry', baseStyle)
        const cardCvc = elementsRef.current.create('cardCvc',    baseStyle)

        // 4. Monter sur les refs DOM (plus fiable que getElementById dans React)
        if (!numDomRef.current || !expDomRef.current || !cvcDomRef.current) {
          throw new Error('Conteneurs DOM introuvables')
        }
        cardNum.mount(numDomRef.current)
        cardExp.mount(expDomRef.current)
        cardCvc.mount(cvcDomRef.current)

        cardNumRef.current = cardNum
        cardExpRef.current = cardExp
        cardCvcRef.current = cardCvc

        const onChange = e => { if (e.error) setError(e.error.message); else setError('') }
        cardNum.on('change', onChange)
        cardExp.on('change', onChange)
        cardCvc.on('change', onChange)

        if (!destroyed) { setStripeReady(true); setStatus('ready') }
      } catch (err) {
        if (!destroyed) { setStatus('error'); setError('Impossible de charger le formulaire de paiement.') }
        console.error('Stripe init error:', err)
      }
    }

    init()
    return () => {
      destroyed = true
      cardNumRef.current?.destroy()
      cardExpRef.current?.destroy()
      cardCvcRef.current?.destroy()
    }
  }, [])

  const handlePay = async () => {
    if (!stripeRef.current || !cardNumRef.current || !cardName.trim()) return
    setLoading(true); setError('')

    try {
      const { error: pmError, paymentMethod } = await stripeRef.current.createPaymentMethod({
        type: 'card',
        card: cardNumRef.current,
        billing_details: { name: cardName.trim(), email: orderInfo?.email || '' },
      })
      if (pmError) throw new Error(pmError.message)

      const response = await fetch(
        'https://us-central1-pour100johnny-app.cloudfunctions.net/api/create-payment-intent',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: total, currency: 'eur',
            customer_data: {
              name: `${orderInfo?.firstName || ''} ${orderInfo?.lastName || ''}`.trim(),
              email: orderInfo?.email || '',
              phone: orderInfo?.phone || '',
            },
            order_data: { reference, cart: [], type: 'abonnement' },
          }),
        }
      )
      if (!response.ok) throw new Error(`Erreur serveur: ${response.status}`)
      const data = await response.json()
      if (!data.success) throw new Error(data.error || 'Erreur serveur')

      const { error: confirmError, paymentIntent } = await stripeRef.current.confirmCardPayment(
        data.client_secret, { payment_method: paymentMethod.id }
      )
      if (confirmError) throw new Error(confirmError.message)

      await saveToFirebase('stripe', paymentIntent.id, orderInfo, reference, total)
      onSuccess('Carte bancaire')
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  const inputWrap = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10, padding: '12px 14px', minHeight: 42,
  }
  const labelStyle = {
    fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Nom sur la carte */}
      <div>
        <label style={labelStyle}>Nom sur la carte *</label>
        <input
          type="text" value={cardName} onChange={e => setCardName(e.target.value)}
          placeholder="Jean Dupont"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10, padding: '11px 14px', color: '#fff', fontSize: '0.9rem', outline: 'none',
          }}
        />
      </div>

      {/* Numéro de carte */}
      <div>
        <label style={labelStyle}>Numéro de carte *</label>
        <div ref={numDomRef} style={inputWrap} />
      </div>

      {/* Expiry + CVC */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Date d'expiration *</label>
          <div ref={expDomRef} style={inputWrap} />
        </div>
        <div>
          <label style={labelStyle}>CVC *</label>
          <div ref={cvcDomRef} style={inputWrap} />
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 9, background: 'rgba(220,20,60,0.1)', border: '1px solid rgba(220,20,60,0.3)', color: '#ff6b6b', fontSize: '0.82rem' }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ padding: '10px 14px', borderRadius: 9, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: '0.9rem' }}>🔒</span>
        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.42)' }}>
          Paiement sécurisé par Stripe · vos données ne transitent pas par nos serveurs
        </span>
      </div>

      {!stripeReady && status === 'loading' && (
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', padding: '6px 0' }}>
          ⏳ Chargement du formulaire sécurisé...
        </div>
      )}
      {status === 'error' && !error && (
        <div style={{ padding: '10px 14px', borderRadius: 9, background: 'rgba(220,20,60,0.1)', border: '1px solid rgba(220,20,60,0.3)', color: '#ff6b6b', fontSize: '0.82rem' }}>
          ⚠️ Impossible de charger Stripe. Vérifiez votre connexion.
        </div>
      )}

      <GoldBtn onClick={handlePay} disabled={loading || !cardName.trim() || !stripeReady} style={{ width: '100%', fontSize: '1rem', padding: '15px' }}>
        {loading ? '⏳ Traitement en cours...' : `🔒 Payer €${total} par carte`}
      </GoldBtn>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// PAYPAL FORM — charge le SDK PayPal dynamiquement
// ══════════════════════════════════════════════════════════
const PAYPAL_CLIENT_ID = 'AX4yXJ9gGOEKa4_LESrCYxZ_zg-F-kg30CBVlKN-UCNIkqEn3c2EJdnS19Y2LvqCVkFHcZVccpv-E5xw'

function PayPalForm({ total, reference, orderInfo, onSuccess }) {
  const [status, setStatus]   = useState('loading') // loading | ready | error
  const containerRef          = useRef(null)
  const renderedRef           = useRef(false)

  useEffect(() => {
    let destroyed = false

    const init = async () => {
      try {
        // 1. Charger le SDK PayPal si pas déjà présent
        if (!window.paypal) {
          await new Promise((resolve, reject) => {
            // Supprimer l'ancien script s'il existe (client-id différent)
            const old = document.querySelector('script[src*="paypal.com/sdk"]')
            if (old) old.remove()

            const s = document.createElement('script')
            s.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=EUR&locale=fr_FR`
            s.async = true
            s.onload  = resolve
            s.onerror = reject
            document.head.appendChild(s)
          })
        }

        if (destroyed) return
        if (!containerRef.current || renderedRef.current) return
        renderedRef.current = true

        // 2. Vider le conteneur et rendre les boutons
        containerRef.current.innerHTML = ''

        window.paypal.Buttons({
          style: { color: 'gold', shape: 'rect', label: 'pay', layout: 'vertical', height: 48 },

          createOrder: (data, actions) => actions.order.create({
            purchase_units: [{
              description: 'Adhésion Association Radio 100% Johnny',
              reference_id: reference,
              amount: {
                currency_code: 'EUR',
                value: total.toFixed(2),
                breakdown: { item_total: { currency_code: 'EUR', value: total.toFixed(2) } },
              },
              items: [{
                name: 'Adhésion 100% Johnny',
                unit_amount: { currency_code: 'EUR', value: total.toFixed(2) },
                quantity: '1',
              }],
            }],
            application_context: {
              brand_name: 'Association Radio 100% Johnny',
              locale: 'fr-FR',
              user_action: 'PAY_NOW',
            },
          }),

          onApprove: async (data, actions) => {
            const details = await actions.order.capture()
            await saveToFirebase('paypal', details.id, orderInfo, reference, total)
            onSuccess('PayPal')
          },

          onError: err => {
            console.error('Erreur PayPal:', err)
            if (!destroyed) setStatus('error')
          },

          onCancel: () => {},
        }).render(containerRef.current)

        if (!destroyed) setStatus('ready')
      } catch (err) {
        console.error('PayPal init error:', err)
        if (!destroyed) setStatus('error')
      }
    }

    init()
    return () => { destroyed = true }
  }, [])

  return (
    <div>
      {status === 'loading' && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
          <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>⏳</div>
          Chargement de PayPal...
        </div>
      )}
      {status === 'error' && (
        <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(220,20,60,0.1)', border: '1px solid rgba(220,20,60,0.3)', color: '#ff6b6b', fontSize: '0.82rem', textAlign: 'center' }}>
          ⚠️ Impossible de charger PayPal. Vérifiez votre connexion.
        </div>
      )}
      <div ref={containerRef} style={{ minHeight: status === 'loading' ? 0 : 50 }} />
      <div style={{ marginTop: 10, fontSize: '0.73rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
        🔒 Paiement sécurisé · vous serez redirigé vers PayPal
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// 💾 SAVE TO FIREBASE → collection association_subscriptions
// ══════════════════════════════════════════════════════════
async function saveToFirebase(method, transactionId, orderInfo, reference, total) {
  try {
    if (typeof firebase === 'undefined') return
    const user = firebase.auth().currentUser

    const paymentData = {
      paymentMethod:    method,
      paymentReference: reference,
      transactionId:    transactionId,
      paymentStatus:    method === 'transfer' ? 'pending' : 'completed',
      paymentDate:      new Date().toISOString(),
      type:             'subscription_with_donation',
      userId:           user ? user.uid : null,
      userEmail:        user ? user.email : (orderInfo?.email || ''),

      // Infos commande
      firstName:    orderInfo?.firstName    || '',
      lastName:     orderInfo?.lastName     || '',
      contactEmail: orderInfo?.email        || '',
      contactPhone: orderInfo?.phone        || '',
      address:      orderInfo?.address      || '',
      city:         orderInfo?.city         || '',
      postalCode:   orderInfo?.postalCode   || '',
      country:      orderInfo?.country      || '',

      // Totaux
      subscriptionTotal: ABONNEMENT.price,
      donationTotal:     Math.max(0, total - ABONNEMENT.price),
      total:             total,

      // Adhésion
      abonnement: ABONNEMENT,
      memberSince: new Date().toISOString(),

      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    }

    // ✅ Enregistrement dans la collection association_subscriptions
    await firebase.firestore().collection('100johnny').doc('main').collection('association_subscriptions').add(paymentData)

    // Mise à jour du profil utilisateur
    if (user) {
      await firebase.firestore().collection('users').doc(user.uid).set({
        isAssociationMember: true,
        membershipDate: new Date().toISOString(),
        lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
        ...(orderInfo?.firstName && { firstName: orderInfo.firstName }),
        ...(orderInfo?.lastName  && { lastName:  orderInfo.lastName  }),
        ...(orderInfo?.phone     && { phone:      orderInfo.phone     }),
        ...(orderInfo?.address   && { address:    orderInfo.address   }),
        ...(orderInfo?.city      && { city:        orderInfo.city      }),
        ...(orderInfo?.postalCode && { postalCode: orderInfo.postalCode }),
        ...(orderInfo?.country   && { country:    orderInfo.country   }),
      }, { merge: true })
    }

    // Supprimer le panier Firebase
    if (user) {
      await firebase.firestore().collection('100johnny').doc('main').collection('subscription_carts').doc(user.uid).delete().catch(() => {})
    }

    console.log('✅ Abonnement enregistré dans association_subscriptions')
  } catch (err) {
    console.error('❌ Erreur Firebase saveToFirebase:', err)
  }
}

// ══════════════════════════════════════════════════════════
// STEP 1 — LANDING
// ══════════════════════════════════════════════════════════
function LandingStep({ onJoin }) {
  const perks = [
    { icon: '🎵', title: 'Accès exclusif',  desc: 'Contenus réservés aux membres' },
    { icon: '🎙️', title: 'Avant-premières', desc: 'Nouveautés en avant-première' },
    { icon: '🤝', title: 'Communauté',      desc: 'Des milliers de fans passionnés' },
    { icon: '💛', title: 'Soutenez',        desc: 'Faites vivre la légende de Johnny' },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(20px,3.5vh,34px)' }}>
      <div style={{ textAlign: 'center', padding: 'clamp(14px,2.5vw,28px) 0' }}>
        <div style={{ marginBottom: 14 }}><StepBadge>Association officielle</StepBadge></div>
        <h1 style={{
          fontSize: 'clamp(1.8rem,5vw,2.9rem)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 14px',
          background: 'linear-gradient(135deg,#fff 30%,#ffd700)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          Rejoignez l'Association<br />100% Johnny
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.52)', fontSize: 'clamp(0.88rem,1.8vw,1rem)', maxWidth: 460, margin: '0 auto 26px' }}>
          Devenez membre et participez activement à la célébration de la légende du rock français.
        </p>
        <Card glow style={{ display: 'inline-block', padding: '24px 40px', textAlign: 'center' }}>
          <div style={{ fontSize: '0.73rem', fontWeight: 700, color: 'rgba(255,255,255,0.42)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
            Adhésion annuelle 2025
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 3, marginBottom: 2 }}>
            <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#ffd700', marginTop: 7 }}>€</span>
            <span style={{ fontSize: '3.6rem', fontWeight: 900, color: '#ffd700', lineHeight: 1 }}>20</span>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.76rem', marginBottom: 20 }}>par an · accès immédiat</div>
          <GoldBtn onClick={onJoin} style={{ width: '100%', fontSize: '0.93rem', padding: '14px 34px' }}>
            ✨ Adhérer maintenant
          </GoldBtn>
        </Card>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        {perks.map((p, i) => (
          <Card key={i} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>{p.icon}</div>
            <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', marginBottom: 4 }}>{p.title}</div>
            <div style={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.42)', lineHeight: 1.5 }}>{p.desc}</div>
          </Card>
        ))}
      </div>
      <div style={{ padding: '13px 16px', borderRadius: 12, background: 'rgba(0,217,255,0.07)', border: '1px solid rgba(0,217,255,0.18)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span>ℹ️</span>
        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.57)', flex: 1 }}>
          Adhésion valable 1 an. Confirmation + carte de membre envoyées par email.
        </span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// STEP 2 — DON
// ══════════════════════════════════════════════════════════
function DonationStep({ onConfirm, onBack }) {
  const [selected, setSelected] = useState(0)
  const [custom,   setCustom]   = useState('')
  const donationAmt = selected === -1 ? Math.max(0, Math.min(parseInt(custom) || 0, 1000)) : selected
  const total = ABONNEMENT.price + donationAmt

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <StepBadge>Étape 1 sur 3</StepBadge>
        <h2 style={{ fontSize: 'clamp(1.1rem,2.8vw,1.6rem)', fontWeight: 900, color: '#fff', margin: '10px 0 5px' }}>
          Souhaitez-vous ajouter un don ?
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.83rem', margin: 0 }}>
          Votre générosité aide l'association à grandir et honorer la mémoire de Johnny.
        </p>
      </div>
      <Card style={{ padding: '13px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>🎵 {ABONNEMENT.name}</div>
          <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.73rem', marginTop: 2 }}>Adhésion annuelle 2025</div>
        </div>
        <div style={{ fontWeight: 900, color: '#ffd700', fontSize: '1.1rem' }}>€{ABONNEMENT.price}</div>
      </Card>
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.42)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
          Montant du don
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DONATION_OPTIONS.map(opt => {
            const sel = selected === opt.amount
            return (
              <button key={opt.amount} onClick={() => { setSelected(opt.amount); if (opt.amount !== -1) setCustom('') }}
                style={{
                  padding: '10px 16px', borderRadius: 11, fontWeight: 800, fontSize: '0.83rem',
                  cursor: 'pointer', outline: 'none', transition: 'all 0.15s',
                  border: `2px solid ${sel ? '#ffd700' : 'rgba(255,255,255,0.1)'}`,
                  background: sel ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.04)',
                  color: sel ? '#ffd700' : 'rgba(255,255,255,0.68)',
                }}>
                {opt.label}
              </button>
            )
          })}
        </div>
        {selected === -1 && (
          <div style={{ marginTop: 11, display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.11)', borderRadius: 10, padding: '10px 14px' }}>
            <span style={{ color: '#ffd700', fontWeight: 900 }}>€</span>
            <input type="number" min="1" max="1000" value={custom} onChange={e => setCustom(e.target.value)}
              placeholder="Montant libre..." autoFocus
              style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '0.9rem', outline: 'none', flex: 1 }} />
          </div>
        )}
      </div>
      <Card glow style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}>Total à payer</div>
        <div style={{ color: '#ffd700', fontWeight: 900, fontSize: '1.4rem' }}>€{total}</div>
      </Card>
      {donationAmt > 0 && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.22)', fontSize: '0.8rem', color: 'rgba(52,211,153,0.9)' }}>
          💛 Merci ! Votre don de €{donationAmt} sera versé directement à l'association.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <GoldBtn variant="ghost" onClick={onBack}>← Retour</GoldBtn>
        <GoldBtn onClick={() => onConfirm({ donation: donationAmt, total })} style={{ flex: 1, minWidth: 130 }}>Continuer →</GoldBtn>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// STEP 3 — PANIER
// ══════════════════════════════════════════════════════════
function CartStep({ cart, onCheckout, onBack, onRemove }) {
  const total = cart.reduce((s, i) => s + i.total, 0)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <StepBadge>Étape 2 sur 3</StepBadge>
        <h2 style={{ fontSize: 'clamp(1.1rem,2.8vw,1.6rem)', fontWeight: 900, color: '#fff', margin: '10px 0 5px' }}>Votre panier 🛒</h2>
      </div>
      {cart.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 34 }}>
          <div style={{ fontSize: '2.6rem', marginBottom: 10 }}>🛒</div>
          <div style={{ color: 'rgba(255,255,255,0.38)' }}>Votre panier est vide</div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cart.map(item => (
            <Card key={item.id} style={{ padding: '13px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0, background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}>🎵</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.9rem' }}>{item.abonnement.name}</div>
                  <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    Adhésion €{item.abonnement.price}
                    {item.donation > 0 && <span style={{ color: 'rgba(52,211,153,0.8)' }}> · Don €{item.donation}</span>}
                  </div>
                </div>
                <div style={{ fontWeight: 900, color: '#ffd700', fontSize: '1.08rem', marginRight: 8 }}>€{item.total}</div>
                <button onClick={() => onRemove(item.id)}
                  style={{ background: 'rgba(220,20,60,0.1)', border: '1px solid rgba(220,20,60,0.28)', borderRadius: 8, color: '#ff6b6b', cursor: 'pointer', padding: '5px 8px', fontSize: '0.73rem', fontWeight: 700, outline: 'none' }}>
                  ✕
                </button>
              </div>
            </Card>
          ))}
          <Card glow style={{ padding: '13px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <div style={{ fontWeight: 700, color: 'rgba(255,255,255,0.62)' }}>Total</div>
            <div style={{ fontWeight: 900, color: '#ffd700', fontSize: '1.3rem' }}>€{total}</div>
          </Card>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <GoldBtn variant="ghost" onClick={onBack}>← Modifier</GoldBtn>
        <GoldBtn onClick={onCheckout} disabled={cart.length === 0} style={{ flex: 1, minWidth: 130 }}>Commander →</GoldBtn>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// STEP 4 — FORMULAIRE
// ══════════════════════════════════════════════════════════
function OrderStep({ onConfirm, onBack, currentUser }) {
  const [form, setForm]           = useState({ firstName:'', lastName:'', email:'', phone:'', address:'', city:'', postalCode:'', country:'France' })
  const [autoFilled, setAutoFill] = useState(false)
  const [loadingData, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser) { setLoading(false); return }
    const load = async () => {
      try {
        if (typeof firebase !== 'undefined') {
          const doc = await firebase.firestore().collection('users').doc(currentUser.uid).get()
          if (doc.exists) {
            const d = doc.data()
            const parts = (d.name || d.displayName || '').split(' ')
            setForm({
              firstName:  d.firstName  || parts[0]                  || '',
              lastName:   d.lastName   || parts.slice(1).join(' ')  || '',
              email:      d.email      || currentUser.email          || '',
              phone:      d.phone      || d.phoneNumber              || '',
              address:    d.address    || '',
              city:       d.city       || '',
              postalCode: d.postalCode || '',
              country:    d.country    || 'France',
            })
            setAutoFill(true)
          } else {
            const dp = (currentUser.displayName || '').split(' ')
            setForm(f => ({ ...f, firstName: dp[0] || '', lastName: dp.slice(1).join(' ') || '', email: currentUser.email || '' }))
          }
        } else {
          setForm(f => ({ ...f, email: currentUser.email || '' }))
        }
      } catch (e) {
        setForm(f => ({ ...f, email: currentUser?.email || '' }))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [currentUser])

  const set = key => val => setForm(f => ({ ...f, [key]: val }))
  const valid = form.firstName.trim() && form.lastName.trim() && form.email.trim()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <StepBadge>Étape 3 sur 3</StepBadge>
        <h2 style={{ fontSize: 'clamp(1.1rem,2.8vw,1.6rem)', fontWeight: 900, color: '#fff', margin: '10px 0 5px' }}>Vos informations</h2>
        {autoFilled && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '4px 11px', borderRadius: 20, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.28)', fontSize: '0.75rem', fontWeight: 700, color: '#34d399' }}>
            ✓ Informations pré-remplies depuis votre profil
          </div>
        )}
      </div>
      {loadingData ? (
        <div style={{ textAlign: 'center', padding: 28, color: 'rgba(255,255,255,0.38)' }}>
          <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>⏳</div>Chargement de vos informations...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldInput label="Prénom"     id="firstName"  required value={form.firstName}  onChange={set('firstName')}  placeholder="Jean"            autoFilled={autoFilled} />
          <FieldInput label="Nom"        id="lastName"   required value={form.lastName}   onChange={set('lastName')}   placeholder="Dupont"          autoFilled={autoFilled} />
          <div style={{ gridColumn:'1/-1' }}>
            <FieldInput label="Email" id="email" type="email" required value={form.email} onChange={set('email')} placeholder="jean@exemple.fr" autoFilled={autoFilled} />
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <FieldInput label="Téléphone" id="phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="+33 6 00 00 00 00" autoFilled={autoFilled} />
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <FieldInput label="Adresse" id="address" value={form.address} onChange={set('address')} placeholder="12 rue des Lilas" autoFilled={autoFilled} />
          </div>
          <FieldInput label="Ville"       id="city"       value={form.city}       onChange={set('city')}       placeholder="Paris" autoFilled={autoFilled} />
          <FieldInput label="Code postal" id="postalCode" value={form.postalCode} onChange={set('postalCode')} placeholder="75001" autoFilled={autoFilled} />
          <div style={{ gridColumn:'1/-1', display:'flex', flexDirection:'column', gap:5 }}>
            <label style={{ fontSize:'0.74rem', fontWeight:700, color:'rgba(255,255,255,0.52)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Pays</label>
            <select value={form.country} onChange={e => set('country')(e.target.value)}
              style={{ width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.11)', borderRadius:10, padding:'11px 14px', color:'#fff', fontSize:'0.9rem', outline:'none', boxSizing:'border-box' }}>
              {['France','Belgique','Suisse','Luxembourg','Canada','Autre'].map(c => (
                <option key={c} value={c} style={{ background:'#1a1a2e' }}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <GoldBtn variant="ghost" onClick={onBack}>← Retour</GoldBtn>
        <GoldBtn onClick={() => onConfirm(form)} disabled={!valid || loadingData} style={{ flex: 1, minWidth: 130 }}>Choisir le paiement →</GoldBtn>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// STEP 5 — PAIEMENT COMPLET
// ══════════════════════════════════════════════════════════
function PaymentStep({ total, reference, orderInfo, onSuccess, onBack }) {
  const [method, setMethod] = useState(null)

  const methods = [
    {
      id: 'paypal',
      iconBg: '#003087',
      iconContent: <span style={{ color: '#009cde', fontWeight: 900, fontSize: '1rem', fontStyle: 'italic' }}>P</span>,
      label: 'PayPal',
      desc: 'Paiement sécurisé PayPal',
    },
    {
      id: 'stripe',
      iconBg: 'linear-gradient(135deg,#1a237e,#3949ab)',
      iconContent: <span style={{ fontSize: '1rem' }}>💳</span>,
      label: 'Carte bancaire',
      desc: 'Visa, Mastercard, CB',
    },
    {
      id: 'transfer',
      iconBg: 'rgba(80,80,100,0.5)',
      iconContent: <span style={{ fontSize: '1rem' }}>🏦</span>,
      label: 'Virement bancaire',
      desc: 'Sous 3-5 jours ouvrés',
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <StepBadge>Paiement</StepBadge>
        <h2 style={{ fontSize: 'clamp(1.1rem,2.8vw,1.6rem)', fontWeight: 900, color: '#fff', margin: '10px 0 5px' }}>
          Choisissez votre moyen de paiement
        </h2>
      </div>

      {/* Récap */}
      <Card style={{ padding: '13px 17px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.8rem' }}>Référence</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#ffd700', fontWeight: 700 }}>{reference}</span>
        </div>
        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '4px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.8rem' }}>Montant total</span>
          <span style={{ color: '#ffd700', fontWeight: 900, fontSize: '1.28rem' }}>€{total}</span>
        </div>
      </Card>

      {/* ✅ Sélection méthode via <div> — jamais de fond jaune */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {methods.map(m => (
          <PayMethodRow key={m.id} m={m} selected={method} onClick={setMethod} />
        ))}
      </div>

      {/* ✅ Panneau Stripe — formulaire complet */}
      {method === 'stripe' && (
        <Card style={{ padding: '18px 20px', borderColor: 'rgba(255,215,0,0.15)', marginTop: 4 }}>
          <div style={{ fontWeight: 700, color: '#ffd700', fontSize: '0.83rem', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            💳 Paiement par carte bancaire
          </div>
          <StripeForm
            total={total}
            reference={reference}
            orderInfo={orderInfo}
            onSuccess={onSuccess}
          />
        </Card>
      )}

      {/* ✅ Panneau PayPal */}
      {method === 'paypal' && (
        <Card style={{ padding: '18px 20px', borderColor: 'rgba(255,215,0,0.15)', marginTop: 4 }}>
          <div style={{ fontWeight: 700, color: '#ffd700', fontSize: '0.83rem', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            🅿️ Paiement PayPal
          </div>
          <PayPalForm
            total={total}
            reference={reference}
            orderInfo={orderInfo}
            onSuccess={onSuccess}
          />
        </Card>
      )}

      {/* ✅ Panneau Virement */}
      {method === 'transfer' && (
        <Card style={{ padding: '16px 20px', borderColor: 'rgba(255,215,0,0.15)', marginTop: 4 }}>
          <div style={{ fontWeight: 700, color: '#ffd700', marginBottom: 12, fontSize: '0.83rem' }}>📋 Coordonnées bancaires</div>
          {[
            ['Bénéficiaire', 'Association Radio 100% Johnny', false],
            ['IBAN',         'FR76 2823 3000 0174 5427 4485 808', true],
            ['BIC',          'REVOFRP2', true],
            ['Référence',    reference, true],
          ].map(([k, v, mono]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.78rem' }}>
              <span style={{ color: 'rgba(255,255,255,0.42)' }}>{k}</span>
              <span style={{ color: '#fff', fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.73rem' : '0.78rem' }}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop: 10, fontSize: '0.73rem', color: 'rgba(255,215,0,0.65)' }}>
            ⚠️ Indiquez la référence dans le libellé du virement.
          </div>
          <GoldBtn
            onClick={async () => {
              await saveToFirebase('transfer', `VIREMENT-${reference}`, orderInfo, reference, total)
              onSuccess('Virement bancaire')
            }}
            style={{ width: '100%', marginTop: 16 }}
          >
            ✅ Confirmer ma demande de virement
          </GoldBtn>
        </Card>
      )}

      {!method && (
        <div style={{ textAlign: 'center', padding: '10px 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.82rem' }}>
          👆 Sélectionnez un moyen de paiement ci-dessus
        </div>
      )}

      <GoldBtn variant="ghost" onClick={onBack}>← Retour</GoldBtn>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// STEP 6 — SUCCÈS
// ══════════════════════════════════════════════════════════
function SuccessStep({ reference, paymentMethod, total, onHome }) {
  const [count, setCount] = useState(5)
  useEffect(() => {
    if (count <= 0) { onHome(); return }
    const t = setTimeout(() => setCount(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [count, onHome])

  return (
    <div style={{ textAlign: 'center', padding: 'clamp(14px,3vw,30px) 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'linear-gradient(135deg,#28a745,#20c997)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2.2rem', boxShadow: '0 0 50px rgba(40,167,69,0.35)',
        animation: 'scaleIn 0.55s cubic-bezier(0.34,1.56,0.64,1)',
      }}>✓</div>
      <div>
        <h2 style={{ fontSize: 'clamp(1.5rem,4vw,2.1rem)', fontWeight: 900, color: '#fff', margin: '0 0 7px' }}>Adhésion confirmée !</h2>
        <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.92rem', margin: 0 }}>Bienvenue dans l'association 100% Johnny 🎵</p>
      </div>
      <Card glow style={{ width: '100%', maxWidth: 340, padding: '20px 24px' }}>
        <div style={{ fontSize: '0.66rem', fontWeight: 700, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 5 }}>Référence</div>
        <div style={{ fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: 900, color: '#ffd700', letterSpacing: '1px', marginBottom: 13 }}>{reference}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 5 }}>
          <span style={{ color: 'rgba(255,255,255,0.38)' }}>Montant payé</span>
          <span style={{ fontWeight: 800, color: '#fff' }}>€{total}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
          <span style={{ color: 'rgba(255,255,255,0.38)' }}>Paiement</span>
          <span style={{ fontWeight: 800, color: '#fff', textTransform: 'capitalize' }}>{paymentMethod}</span>
        </div>
      </Card>
      <div style={{ padding: '11px 16px', borderRadius: 11, background: 'rgba(220,20,60,0.08)', border: '1px solid rgba(220,20,60,0.2)', fontSize: '0.8rem', color: 'rgba(255,155,155,0.85)', maxWidth: 320 }}>
        ❤️ Merci de faire vivre la légende de Johnny avec nous !
      </div>
      <div style={{ color: '#ffd700', fontWeight: 700, fontSize: '0.86rem' }}>
        Redirection dans <span style={{ fontSize: '1.2rem', fontWeight: 900 }}>{count}</span>s…
      </div>
      <GoldBtn onClick={onHome}>🏠 Retour à l'accueil</GoldBtn>
      <style>{`
        @keyframes scaleIn {
          from { transform:scale(0); opacity:0; }
          60%  { transform:scale(1.15); }
          to   { transform:scale(1);   opacity:1; }
        }
      `}</style>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════
export default function AbonnementPage({ currentUser }) {
  const [step,        setStep]  = useState(STEPS.LANDING)
  const [cart,        setCart]  = useState([])
  const [orderInfo,   setOrder] = useState(null)
  const [successData, setSuc]   = useState(null)
  const [reference]             = useState(generateRef)

  const handleJoin             = ()                  => setStep(STEPS.DONATION)
  const handleDonationConfirm  = ({ donation, total }) => { setCart([{ id: Date.now(), abonnement: ABONNEMENT, donation, total }]); setStep(STEPS.CART) }
  const handleRemove           = id                  => setCart(c => c.filter(i => i.id !== id))
  const handleCheckout         = ()                  => { if (!currentUser) { alert('Vous devez être connecté.'); return }; setStep(STEPS.ORDER) }
  const handleOrderConfirm     = form                => { setOrder(form); setStep(STEPS.PAYMENT) }
  const handlePaymentSuccess   = method              => { setSuc({ method, total: cart.reduce((s, i) => s + i.total, 0) }); setCart([]); setStep(STEPS.SUCCESS) }
  const handleHome             = ()                  => setStep(STEPS.LANDING)

  const stepOrder = [STEPS.LANDING, STEPS.DONATION, STEPS.CART, STEPS.ORDER, STEPS.PAYMENT, STEPS.SUCCESS]
  const progress  = Math.round((stepOrder.indexOf(step) / (stepOrder.length - 1)) * 100)

  return (
    <div className="page active">
      {/* 🛒 Cart icon — bottom:74px pour être AU-DESSUS de la DockBar */}
      <CartIcon count={cart.length} onClick={() => setStep(STEPS.CART)} />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: 'clamp(14px,2.5vw,28px)', paddingBottom: 120, boxSizing: 'border-box' }}>
        <div className="page-header" style={{ marginBottom: 'clamp(12px,2.5vh,22px)' }}>
          <div className="page-badge">🎸 ASSOCIATION</div>
          <h1 className="page-title">Adhésion 2025</h1>
          <p className="page-subtitle">Association Radio 100% Johnny Hallyday</p>
        </div>

        {step !== STEPS.LANDING && step !== STEPS.SUCCESS && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', fontWeight: 700 }}>Progression</span>
              <span style={{ fontSize: '0.7rem', color: '#ffd700', fontWeight: 700 }}>{progress}%</span>
            </div>
            <div style={{ height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#ffd700,#ffe566)', borderRadius: 2, transition: 'width 0.4s ease' }} />
            </div>
          </div>
        )}

        <Card style={{ minHeight: 260 }}>
          {step === STEPS.LANDING  && <LandingStep onJoin={handleJoin} />}
          {step === STEPS.DONATION && <DonationStep onConfirm={handleDonationConfirm} onBack={() => setStep(STEPS.LANDING)} />}
          {step === STEPS.CART     && <CartStep cart={cart} onCheckout={handleCheckout} onBack={() => setStep(STEPS.DONATION)} onRemove={handleRemove} />}
          {step === STEPS.ORDER    && <OrderStep onConfirm={handleOrderConfirm} onBack={() => setStep(STEPS.CART)} currentUser={currentUser} />}
          {step === STEPS.PAYMENT  && (
            <PaymentStep
              total={cart.reduce((s, i) => s + i.total, 0)}
              reference={reference}
              orderInfo={orderInfo}
              onSuccess={handlePaymentSuccess}
              onBack={() => setStep(STEPS.ORDER)}
            />
          )}
          {step === STEPS.SUCCESS  && (
            <SuccessStep
              reference={reference}
              paymentMethod={successData?.method}
              total={successData?.total}
              onHome={handleHome}
            />
          )}
        </Card>
      </div>
    </div>
  )
}